import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Lock,
  LogOut,
  Search,
  Download,
  RefreshCw,
  Loader2,
  Mail,
  Phone,
  Building2,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { adminAuth } from "@/lib/adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginScreen({ onAuthed }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token.trim()) {
      setError("Ingresa el token de acceso.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/verify`,
        {},
        { headers: { Authorization: `Bearer ${token.trim()}` }, timeout: 12000 }
      );
      adminAuth.set(token.trim());
      toast.success("Acceso concedido.");
      onAuthed();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 401
          ? "Token inválido."
          : status === 503
          ? "Admin no configurado en el servidor."
          : "Error de conexión. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-white mb-12"
        >
          <ArrowLeft size={14} /> Volver al sitio
        </Link>

        <div className="border border-white/10 bg-[#111] p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={16} className="text-[#0047FF]" strokeWidth={1.8} />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3]">
              Acceso administrador
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-2">
            Panel de Leads
          </h1>
          <p className="text-[#A3A3A3] text-sm leading-relaxed mb-8">
            Ingresa tu token de acceso para ver los leads recibidos desde el
            formulario de contacto.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-3">
                Token
              </label>
              <input
                data-testid="admin-token-input"
                type="password"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  setError("");
                }}
                placeholder="••••••••••••••••"
                autoComplete="off"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#0047FF] outline-none px-0 py-3 text-base text-white placeholder:text-[#525252] transition-colors"
              />
              {error && (
                <p
                  data-testid="admin-login-error"
                  className="mt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF3B30]"
                >
                  {error}
                </p>
              )}
            </div>

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#0047FF] hover:bg-[#0039CC] text-white font-mono text-[11px] uppercase tracking-[0.18em] py-4 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Verificando…
                </>
              ) : (
                <>
                  Entrar al panel <ArrowUpRight size={14} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#525252] mt-8 text-center">
          Acceso restringido · Wok Center Operations
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-3">
        {label}
      </div>
      <div
        className={`font-display text-3xl md:text-4xl font-black tracking-tight ${
          accent ? "text-[#0047FF]" : "text-[#171717]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchAll = async (search = "", silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${adminAuth.get()}` };
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/leads/stats`, { headers }),
        axios.get(`${API}/admin/leads`, {
          headers,
          params: search ? { q: search, limit: 200 } : { limit: 200 },
        }),
      ]);
      setStats(s.data);
      setLeads(l.data.leads || []);
      setTotal(l.data.total || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada. Vuelve a iniciar sesión.");
        adminAuth.clear();
        onLogout();
        return;
      }
      toast.error("No se pudieron cargar los leads.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchAll(q, true), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: { Authorization: `Bearer ${adminAuth.get()}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `leads_wok_center_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado.");
    } catch {
      toast.error("Error al exportar CSV.");
    }
  };

  const totalShown = useMemo(() => leads.length, [leads]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      {/* Top bar */}
      <header className="bg-[#0A0A0A] text-white">
        <div className="px-6 md:px-10 max-w-[1440px] mx-auto h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 bg-[#0047FF]" />
            <span className="font-display font-black tracking-tight">
              Admin · Wok Center
            </span>
            <span className="hidden md:inline-block ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] border-l border-white/15 pl-3">
              Operations Panel
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-go-site"
              className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3] hover:text-white"
            >
              Ver sitio <ArrowUpRight size={13} />
            </Link>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-[#0A0A0A] transition-colors"
            >
              <LogOut size={13} /> Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 max-w-[1440px] mx-auto py-10 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#525252]">
              Resumen
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">
              Leads recibidos
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => {
                setRefreshing(true);
                fetchAll(q);
              }}
              className="inline-flex items-center gap-2 border border-[#171717] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#171717] hover:text-white transition-colors"
            >
              {refreshing ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              Refrescar
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={handleExport}
              className="inline-flex items-center gap-2 bg-[#0047FF] hover:bg-[#0039CC] text-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
            >
              <Download size={13} /> Exportar CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatCard label="Total leads" value={stats?.total ?? "—"} />
          <StatCard label="Últimos 7 días" value={stats?.last_7_days ?? "—"} accent />
          <StatCard
            label="Emails enviados"
            value={stats?.email_sent_count ?? "—"}
          />
          <StatCard
            label="% con email"
            value={stats ? `${stats.email_sent_percent}%` : "—"}
          />
        </div>

        {/* Search */}
        <div className="bg-white border border-[#E5E5E5] p-5 md:p-6 mb-6 flex items-center gap-3">
          <Search size={16} className="text-[#525252]" />
          <input
            data-testid="admin-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, empresa, email, teléfono o requerimiento…"
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[#A3A3A3]"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#525252] hover:text-[#171717]"
              data-testid="admin-clear-search"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E5E5] overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#525252]">
              Mostrando {totalShown} de {total}
            </div>
          </div>

          {loading ? (
            <div className="py-24 flex items-center justify-center text-[#525252]">
              <Loader2 size={20} className="animate-spin mr-3" />
              Cargando leads…
            </div>
          ) : leads.length === 0 ? (
            <div
              data-testid="admin-empty"
              className="py-24 text-center text-[#525252]"
            >
              <div className="font-display text-2xl font-bold text-[#171717] mb-2">
                Sin leads aún
              </div>
              <p className="text-sm">
                Cuando alguien llene el formulario, lo verás aquí.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[14px]">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#525252]">
                    <th className="px-5 py-4 font-medium">Fecha</th>
                    <th className="px-5 py-4 font-medium">Nombre</th>
                    <th className="px-5 py-4 font-medium">Empresa</th>
                    <th className="px-5 py-4 font-medium">Contacto</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${l.id}`}
                      className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                      onClick={() => setSelected(l)}
                    >
                      <td className="px-5 py-4 font-mono text-[12px] text-[#525252] whitespace-nowrap">
                        {fmtDate(l.created_at)}
                      </td>
                      <td className="px-5 py-4 font-medium text-[#171717]">
                        {l.nombre}
                      </td>
                      <td className="px-5 py-4 text-[#171717]">
                        <span className="inline-flex items-center gap-2">
                          <Building2 size={13} className="text-[#A3A3A3]" />
                          {l.empresa}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[#525252] text-[13px]">
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-2">
                            <Mail size={12} className="text-[#A3A3A3]" />
                            {l.email}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <Phone size={12} className="text-[#A3A3A3]" />
                            {l.telefono}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {l.email_sent ? (
                          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#0047FF]">
                            <CheckCircle2 size={12} /> Enviado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                            <XCircle size={12} /> Sin envío
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0047FF]">
                          Ver →
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail panel (drawer) */}
      {selected && (
        <div
          data-testid="admin-lead-detail"
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <aside
            className="bg-white w-full max-w-xl h-full overflow-y-auto p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#525252]">
                Lead · {selected.id.slice(0, 8)}
              </span>
              <button
                data-testid="admin-detail-close"
                onClick={() => setSelected(null)}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#525252] hover:text-[#171717]"
              >
                Cerrar ✕
              </button>
            </div>

            <h2 className="font-display text-3xl font-black tracking-tight mb-1">
              {selected.nombre}
            </h2>
            <p className="text-[#525252] mb-8">{selected.empresa}</p>

            <div className="space-y-5 border-t border-[#E5E5E5] pt-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1">
                  Email
                </div>
                <a
                  href={`mailto:${selected.email}`}
                  className="text-[#0047FF] hover:underline break-all"
                >
                  {selected.email}
                </a>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1">
                  Teléfono
                </div>
                <a
                  href={`tel:${selected.telefono}`}
                  className="text-[#171717] hover:underline"
                >
                  {selected.telefono}
                </a>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1">
                  Recibido
                </div>
                <div className="text-[#171717]">{fmtDate(selected.created_at)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1">
                  Email enviado
                </div>
                <div className="text-[#171717]">
                  {selected.email_sent ? "Sí" : "No (modo log o key no configurada)"}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
                  Requerimiento
                </div>
                <div className="bg-[#FAFAFA] border-l-2 border-[#0047FF] p-5 text-[#171717] leading-relaxed whitespace-pre-wrap">
                  {selected.requerimiento}
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#E5E5E5] flex gap-3">
              <a
                href={`mailto:${selected.email}?subject=Re%3A%20Tu%20solicitud%20%E2%80%94%20Wok%20Center`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0047FF] hover:bg-[#0039CC] text-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
                data-testid="admin-detail-email"
              >
                <Mail size={13} /> Responder por email
              </a>
              <a
                href={`tel:${selected.telefono}`}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#171717] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#171717] hover:text-white transition-colors"
                data-testid="admin-detail-call"
              >
                <Phone size={13} /> Llamar
              </a>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(Boolean(adminAuth.get()));

  // Validate stored token on mount; clear if invalid
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        await axios.post(
          `${API}/admin/verify`,
          {},
          { headers: { Authorization: `Bearer ${adminAuth.get()}` }, timeout: 8000 }
        );
      } catch (err) {
        if (err?.response?.status === 401) {
          adminAuth.clear();
          setAuthed(false);
        }
      }
    })();
  }, [authed]);

  const handleLogout = () => {
    adminAuth.clear();
    setAuthed(false);
    toast.success("Sesión cerrada.");
    navigate("/admin");
  };

  return authed ? (
    <Dashboard onLogout={handleLogout} />
  ) : (
    <LoginScreen onAuthed={() => setAuthed(true)} />
  );
}
